import { LoadingSpinner } from '@components/loadingSpinner';
import { Modal } from '@components/modal';
import { type FC, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { t } from 'i18next';
import useHRForm from 'hooks/useHRForm';
import useDisclosure from 'hooks/useDisclosure';
import {
  useDeleteCustomProperty,
  useDeleteTag,
  useFetchTag,
  useSetCustomProperty,
  useUpdateTag,
} from 'api/customProperty/customPropertyService';
import { manageTagSchema } from '../../form/ManageTagForm/schema';
import { ManageTagForm } from '../../form/ManageTagForm';
import DeleteTagModal from '../DeleteTagModal';

interface EditTagModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  selectedTagId?: number;
}

const EditTagModal: FC<EditTagModalProps> = ({
  isOpen,
  handleClose,
  handleOpen,
  selectedTagId,
}) => {
  const {
    isOpen: isDeleteOpen,
    handleOpen: handleDeleteOpen,
    handleClose: handleDeleteClose,
  } = useDisclosure();

  const { isFetched, data: tagData } = useFetchTag(selectedTagId);
  const { mutate: deleteTagMutate } = useDeleteTag();
  const { mutate: deleteCustomPropertyMutate } = useDeleteCustomProperty();
  const {
    mutate: updateTagMutate,
    isSuccess,
    isPending,
  } = useUpdateTag(selectedTagId);
  const { mutate: setCustomPropertyMutate } = useSetCustomProperty();
  const initialValues = useMemo(() => {
    if (!tagData) return;
    return {
      ...tagData?.property_definition,
      name: tagData?.property_definition.name ?? '',
      buildings:
        tagData?.assets?.map((asset) => ({
          value: asset.id,
          name: asset.name,
        })) ?? [],
    };
  }, [tagData]);

  const { methods } = useHRForm({
    schema: manageTagSchema,
    values: initialValues,
  });

  const closeModal = (): void => {
    methods.reset(initialValues);
    handleClose();
  };

  const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const switchUnderlineToWhitespace = (str: string): string => {
    return str.replace(/_/g, ' ');
  };

  const getInitialAssets = (): number[] => {
    return tagData?.assets?.map((asset) => asset.id) ?? [];
  };

  const filterRemovedAssets = (assetParams: number[]): number[] => {
    return getInitialAssets().filter(
      (assetId) => !assetParams.includes(assetId)
    );
  };

  const filterAddedAssets = (assetParams: number[]): number[] => {
    return assetParams.filter(
      (assetId) => !getInitialAssets().includes(assetId)
    );
  };

  const handleEditTag = async (): Promise<void> => {
    const formData = methods.getValues();
    const removedAssets = filterRemovedAssets(
      formData.buildings.map((building) => building.value) as number[]
    );
    const addedAssets = filterAddedAssets(
      formData.buildings.map((building) => building.value) as number[]
    );
    const params = {
      name: formData.name,
      display_name: capitalizeFirstLetter(
        switchUnderlineToWhitespace(formData.name)
      ),
      description: formData.description,
    };

    updateTagMutate(params);

    addedAssets.forEach((assetId) => {
      setCustomPropertyMutate({
        params: {
          property_definition_id: selectedTagId as number,
          value: tagData?.property_definition.data_type as string,
        },
        assetId,
      });
    });
    removedAssets.forEach((assetId) => {
      deleteCustomPropertyMutate({
        propertyDefinitionId: selectedTagId as number,
        assetId,
      });
    });
  };

  const handleDeleteTag = (): void => {
    if (selectedTagId) {
      deleteTagMutate(selectedTagId);
      handleDeleteClose();
      handleClose();
    }
  };

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      onOpen={handleOpen}
      actionBtns={{
        primary: {
          label: t('common.buttons.saveChanges'),
          onClick: methods.handleSubmit(handleEditTag),
        },
        secondary: { label: t('common.buttons.cancel') },
      }}
      title={t('assetsPage.TagsTab.label.edit')}
      maxWidth={600}
    >
      {isFetched && !isPending ? (
        <>
          <div className="flex justify-end">
            <p
              className="text-secondary-dark text-base cursor-pointer"
              onClick={handleDeleteOpen}
            >
              {t('assetsPage.TagsTab.label.delete')}
            </p>
          </div>
          <FormProvider {...methods}>
            <ManageTagForm methods={methods} initialValues={initialValues} />
          </FormProvider>
          <DeleteTagModal
            tagId={selectedTagId}
            isOpen={isDeleteOpen}
            handleOpen={handleDeleteOpen}
            handleClose={handleDeleteClose}
            onConfirm={handleDeleteTag}
            isLoading={false}
          />
        </>
      ) : (
        <LoadingSpinner />
      )}
    </Modal>
  );
};

export default EditTagModal;
